import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Star,
  ChevronDown,
  ChevronUp,
  Vote,
  DollarSign,
  ShoppingCart,
  Smartphone,
  Building2,
  Users,
  FileSearch,
  MessageCircle,
} from 'lucide-react';
import { UsePricingCalculatorReturn } from '@/hooks/communities/montana';
import { useMontanaData } from '@/hooks/communities/montana';
import { formatCurrency, formatNumber } from '@/utils/entities/city/montana';
import { useNavigate } from 'react-router-dom';
import { pricingAnalytics } from '@/utils/entities/clients/pricingTracking';

interface PricingCalculatorProps {
  onSelectTier?: (tier: 'silver' | 'gold' | 'custom') => void;
  onContactSales?: () => void;
  onScheduleDemo?: () => void;
  pricingState: UsePricingCalculatorReturn;
  extraNavigateSearch?: string; // e.g., "?addons=a,b"
}

export const PricingCalculator: React.FC<PricingCalculatorProps> = ({
  onSelectTier,
  onContactSales,
  onScheduleDemo,
  pricingState,
  extraNavigateSearch,
}) => {
  const { parcels, pricing, selectedTier, setSelectedTier } = pricingState;
  const { pricingTiers } = useMontanaData();
  const [expandedTiers, setExpandedTiers] = useState<Set<string>>(new Set());
  const navigate = useNavigate();

  // Parse parcels string to number (remove commas)
  const parcelsNumber = parseInt(parcels.toString().replace(/,/g, ''), 10) || 0;

  const handleSelectTier = (tier: 'silver' | 'gold' | 'custom') => {
    console.log('handleSelectTier called with tier:', tier);

    try {
      setSelectedTier(tier);

      // Track analytics
      console.log('Tracking tier selection analytics...');
      pricingAnalytics.trackTierSelection(tier, 'pricing_calculator');

      // Use parent handler if provided, otherwise fall back to local navigation
      if (onSelectTier) {
        console.log('Using parent onSelectTier handler');
        onSelectTier(tier);
      } else {
        // Fallback navigation if no parent handler
        const targetPath =
          tier === 'custom' ? '/onboarding/welcome/contact-sales' : `/onboarding/welcome/${tier}`;
        const fullPath = extraNavigateSearch ? `${targetPath}${extraNavigateSearch}` : targetPath;

        console.log('Navigating to:', fullPath);
        navigate(fullPath);
      }

      console.log('handleSelectTier completed successfully');
    } catch (error) {
      console.error('Error in handleSelectTier:', error);
    }
  };

  const handleContactSalesClick = () => {
    console.log('handleContactSalesClick called');

    try {
      // Track analytics
      console.log('Tracking contact sales analytics...');
      pricingAnalytics.trackContactSales('pricing_calculator');

      // Use parent handler if provided, otherwise fall back to local navigation
      if (onContactSales) {
        console.log('Using parent onContactSales handler');
        onContactSales();
      } else {
        // Fallback navigation if no parent handler
        const path = '/onboarding/welcome/contact-sales';
        const fullPath = extraNavigateSearch ? `${path}${extraNavigateSearch}` : path;
        console.log('Navigating to:', fullPath);
        navigate(fullPath);
      }

      console.log('handleContactSalesClick completed successfully');
    } catch (error) {
      console.error('Error in handleContactSalesClick:', error);
    }
  };

  const toggleTierExpansion = (tierId: string) => {
    const newExpanded = new Set(expandedTiers);
    if (newExpanded.has(tierId)) {
      newExpanded.delete(tierId);
    } else {
      newExpanded.add(tierId);
    }
    setExpandedTiers(newExpanded);
  };

  // Helper function to get feature icon
  const getFeatureIcon = (feature: string) => {
    if (
      feature.toLowerCase().includes('voting') ||
      feature.toLowerCase().includes('decision') ||
      feature.toLowerCase().includes('proposal')
    ) {
      return <Vote className="w-4 h-4 text-blue-500" />;
    }
    if (
      feature.toLowerCase().includes('wealth') ||
      feature.toLowerCase().includes('treasury') ||
      feature.toLowerCase().includes('investment') ||
      feature.toLowerCase().includes('revenue')
    ) {
      return <DollarSign className="w-4 h-4 text-green-500" />;
    }
    if (
      feature.toLowerCase().includes('marketplace') ||
      feature.toLowerCase().includes('buying') ||
      feature.toLowerCase().includes('vendor') ||
      feature.toLowerCase().includes('purchasing')
    ) {
      return <ShoppingCart className="w-4 h-4 text-purple-500" />;
    }
    if (
      feature.toLowerCase().includes('mobile') ||
      feature.toLowerCase().includes('phone') ||
      feature.toLowerCase().includes('app') ||
      feature.toLowerCase().includes('messaging')
    ) {
      return <Smartphone className="w-4 h-4 text-orange-500" />;
    }
    if (
      feature.toLowerCase().includes('infrastructure') ||
      feature.toLowerCase().includes('asset') ||
      feature.toLowerCase().includes('maintenance') ||
      feature.toLowerCase().includes('banking')
    ) {
      return <Building2 className="w-4 h-4 text-slate-500" />;
    }
    return <Users className="w-4 h-4 text-indigo-500" />;
  };

  return (
    <div className="py-16 bg-gray-50">
      <div className="container mx-auto px-4">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Choose Your Perfect Plan
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-6">
            Revolutionary features for{' '}
            <span className="font-semibold text-blue-600">
              {formatNumber(parcelsNumber)} parcels
            </span>
            . Transform your community operations with digital governance and financial management
            tools.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <Vote className="w-4 h-4 text-blue-500" />
              <span>Verifiable Community Voting</span>
            </div>
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-green-500" />
              <span>Financial Transparency</span>
            </div>
            <div className="flex items-center gap-2">
              <FileSearch className="w-4 h-4 text-purple-500" />
              <span>Searchable Meeting Records</span>
            </div>
            <div className="flex items-center gap-2">
              <MessageCircle className="w-4 h-4 text-orange-500" />
              <span>Direct Community Messaging</span>
            </div>
          </div>
        </div>

        {/* Bridge to Revolutionary Features */}
        <div className="bg-linear-to-r from-slate-50 to-blue-50 rounded-2xl p-6 mb-12 max-w-4xl mx-auto border border-blue-100">
          <div className="text-center">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              These aren't just HOA management features...
            </h3>
            <p className="text-gray-600 mb-4">
              Each plan includes capabilities built specifically for community governance and
              transparency. See the detailed breakdown above for what makes us different.
            </p>
            <div className="flex flex-wrap justify-center gap-3 text-xs">
              <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                Verifiable Voting
              </span>
              <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full">
                Financial Transparency
              </span>
              <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full">
                Meeting Records
              </span>
            </div>
          </div>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-2 gap-8 max-w-7xl mx-auto mb-16 pt-12">
          {pricingTiers.map((tier, index) => {
            const tierPricing = pricing[tier.id];
            const isPopular = tier.popular;
            const isSelected = selectedTier === tier.id;
            const isExpanded = expandedTiers.has(tier.id);
            const visibleFeatures = isExpanded ? tier.features : tier.features.slice(0, 8);
            const remainingFeatures = tier.features.length - 8;

            return (
              <Card
                key={tier.id}
                className={`relative transition-all duration-300 hover:shadow-xl ${
                  isPopular
                    ? 'border-blue-500 shadow-lg scale-105'
                    : isSelected
                      ? 'border-green-500 shadow-lg'
                      : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                {/* Popular Badge */}
                {isPopular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <Badge className="bg-blue-500 text-white px-4 py-1 text-sm font-semibold">
                      <Star className="w-4 h-4 mr-1" />
                      {tier.badge}
                    </Badge>
                  </div>
                )}

                <CardHeader className="text-center pb-8 pt-8">
                  <CardTitle className="text-2xl font-bold text-gray-900">{tier.name}</CardTitle>
                  <CardDescription className="text-lg text-gray-600 mt-2">
                    {tier.description}
                  </CardDescription>

                  {/* Pricing Display */}
                  <div className="mt-6">
                    {tier.id === 'custom' ? (
                      // Custom Package - No Pricing
                      <div className="text-center">
                        <div className="text-4xl font-bold text-gray-900 mb-2">Custom Quote</div>
                        <div className="text-lg text-gray-600">Tailored to your needs</div>
                        <div className="text-sm text-gray-500 mt-2">Contact sales for pricing</div>
                      </div>
                    ) : (
                      // Silver/Gold Packages - Show Pricing
                      <>
                        <div className="flex items-baseline justify-center gap-2">
                          <span className="text-5xl font-bold text-gray-900">
                            ${tier.pricePerParcel}
                          </span>
                          <span className="text-gray-600">/parcel/month</span>
                        </div>
                        <div className="text-lg text-gray-700 mt-2 font-medium">
                          Total: {formatCurrency(tierPricing.monthly)}/month
                        </div>
                        <div className="text-sm text-gray-500 mt-1">
                          for {formatNumber(parcelsNumber)} parcels
                        </div>
                        <div className="text-sm text-gray-600 mt-1 flex items-center justify-center gap-1">
                          <span className="font-medium">Setup Fee:</span>{' '}
                          {formatCurrency(tierPricing.setup)}{' '}
                          <span className="text-gray-500">(one-time)</span>
                        </div>
                        <div className="text-xs text-gray-500">
                          {tier.id === 'silver'
                            ? '$30 per parcel'
                            : tier.id === 'gold'
                              ? '$60 per parcel'
                              : null}
                        </div>
                      </>
                    )}
                  </div>
                </CardHeader>

                <CardContent className="pt-0">
                  {/* Term & Notes */}
                  {tier.id !== 'custom' && (
                    <div className="mb-4 text-center text-xs text-gray-600">
                      3-year minimum term. Setup fees exclude analog-to-digital conversion services.
                    </div>
                  )}

                  {/* Transparent Data Usage Pricing */}
                  <div className="mb-6 p-3 bg-linear-to-r from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      <span className="font-semibold text-blue-900 text-sm">
                        Transparent Pricing: Platform + Usage
                      </span>
                    </div>
                    <div className="space-y-1.5 text-xs text-blue-800">
                      <div>
                        <span className="font-medium text-blue-900">
                          Platform Service (Fixed Monthly):
                        </span>
                        <div className="ml-2 text-blue-700">
                          {tier.id === 'silver' && '• $8/parcel/month - Core community management'}
                          {tier.id === 'gold' &&
                            '• $10/parcel/month - Advanced features + analytics'}
                          {tier.id === 'custom' &&
                            '• Quote-based - Enterprise features + customization'}
                        </div>
                      </div>
                      <div>
                        <span className="font-medium text-blue-900">
                          Data Usage (Variable Monthly):
                        </span>
                        <div className="ml-2 text-blue-700 space-y-0.5">
                          <div>• Based on actual community usage patterns</div>
                          <div>• Covers storage, bandwidth, and streaming costs</div>
                          <div>• Detailed monthly usage reports provided</div>
                          <div>
                            •{' '}
                            <span className="font-medium">
                              Estimated range: $0.10-$1.50 per parcel
                            </span>
                          </div>
                          <div className="text-blue-600 italic text-[10px]">
                            *Actual costs vary significantly by usage patterns
                          </div>
                        </div>
                      </div>
                      <div className="mt-1.5 p-1.5 bg-blue-100 rounded text-blue-800 text-[10px]">
                        <span className="italic">
                          Why separate? Like your utility bill, data costs scale with usage. A
                          community using mostly text pays less than one sharing lots of videos.
                        </span>
                      </div>
                    </div>
                  </div>
                  {/* Revolutionary Feature Callout */}
                  {tier.id === 'silver' && (
                    <div className="mb-6 p-4 bg-linear-to-r from-blue-50 to-green-50 rounded-lg border border-blue-200">
                      <div className="flex items-center gap-2 mb-2">
                        <DollarSign className="w-5 h-5 text-green-600" />
                        <span className="font-semibold text-green-800">
                          Community Financial Tools
                        </span>
                      </div>
                      <p className="text-sm text-green-700">
                        Track HOA treasury, coordinate group purchasing for potential service
                        discounts, and manage community finances transparently.
                      </p>
                    </div>
                  )}

                  {tier.id === 'gold' && (
                    <div className="mb-6 p-4 bg-linear-to-r from-purple-50 to-blue-50 rounded-lg border border-purple-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Building2 className="w-5 h-5 text-purple-600" />
                        <span className="font-semibold text-purple-800">
                          Advanced Governance & Integration
                        </span>
                      </div>
                      <p className="text-sm text-purple-700">
                        Enhanced support with phone access for critical issues (4hr response),
                        faster SLA response times, and advanced platform features.
                      </p>
                    </div>
                  )}

                  {tier.id === 'custom' && (
                    <div className="mb-6 p-4 bg-linear-to-r from-indigo-50 to-purple-50 rounded-lg border border-indigo-200">
                      <div className="flex items-center gap-2 mb-2">
                        <Star className="w-5 h-5 text-indigo-600" />
                        <span className="font-semibold text-indigo-800">
                          Unlimited Transformation
                        </span>
                      </div>
                      <p className="text-sm text-indigo-700">
                        Complete community transformation with dedicated development team and
                        unlimited customization possibilities.
                      </p>
                    </div>
                  )}

                  {/* CTA Button */}
                  <Button
                    className={`w-full mb-6 text-lg py-6 font-semibold transition-all duration-300 ${
                      tier.id === 'custom'
                        ? 'bg-linear-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl'
                        : tier.id === 'silver'
                          ? 'bg-linear-to-r from-gray-400 to-gray-500 hover:from-gray-500 hover:to-gray-600 text-white shadow-md hover:shadow-lg'
                          : tier.id === 'gold'
                            ? 'bg-linear-to-r from-yellow-400 via-yellow-500 to-amber-500 hover:from-yellow-500 hover:via-yellow-600 hover:to-amber-600 text-white shadow-lg hover:shadow-2xl transform hover:scale-105'
                            : isSelected
                              ? 'bg-green-600 hover:bg-green-700 text-white'
                              : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                    onClick={() => {
                      console.log('Button clicked for tier:', tier.id, 'tier.name:', tier.name);
                      console.log('isSelected:', isSelected, 'isPopular:', isPopular);
                      if (tier.id === 'custom') {
                        console.log('Calling handleContactSalesClick...');
                        handleContactSalesClick();
                      } else {
                        console.log('Calling handleSelectTier with:', tier.id);
                        handleSelectTier(tier.id);
                      }
                    }}
                  >
                    {tier.id === 'custom' ? tier.ctaText : isSelected ? 'Selected' : tier.ctaText}
                  </Button>

                  {/* Features List */}
                  <div className="space-y-3">
                    <div className="font-semibold text-gray-900 text-sm mb-4 flex items-center gap-2">
                      <span className="w-2 h-2 bg-linear-to-r from-blue-500 to-green-500 rounded-full"></span>
                      Revolutionary Features Included:
                    </div>
                    {visibleFeatures.map((feature, featureIndex) => (
                      <div key={featureIndex} className="flex items-start gap-3 group">
                        {getFeatureIcon(feature)}
                        <div className="flex-1">
                          <span className="text-sm text-gray-700 group-hover:text-gray-900 transition-colors">
                            {feature}
                          </span>
                          {/* Add special badges for unique features */}
                          {(feature.includes('Secure voting') ||
                            feature.includes('Direct bank') ||
                            feature.includes('Revenue sharing') ||
                            feature.includes('30 seconds')) && (
                            <Badge className="ml-2 bg-linear-to-r from-purple-500 to-blue-500 text-white text-xs px-2 py-0">
                              UNIQUE
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}

                    {remainingFeatures > 0 && (
                      <button
                        onClick={() => toggleTierExpansion(tier.id)}
                        className="flex items-center gap-2 text-sm text-blue-600 font-medium hover:text-blue-700 transition-colors mt-4 p-2 hover:bg-blue-50 rounded-lg w-full justify-center"
                      >
                        {isExpanded ? (
                          <>
                            <ChevronUp className="w-4 h-4" />
                            Show less features
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-4 h-4" />+ {remainingFeatures} more
                            revolutionary features
                          </>
                        )}
                      </button>
                    )}
                  </div>

                  {/* Annual Savings */}
                  {tier.id !== 'custom' && (
                    <div className="mt-6 p-4 bg-green-50 rounded-lg">
                      <div className="text-sm font-medium text-green-800">
                        Annual Total: {formatCurrency(tierPricing.annual)}
                      </div>
                      <div className="text-xs text-green-600">Save 10% with annual billing</div>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Need something else */}
        <div className="text-center mb-12 max-w-3xl mx-auto">
          <p className="text-gray-600">
            Need something else?{' '}
            <button
              type="button"
              onClick={() => navigate('/onboarding/welcome/contact-sales')}
              className="text-blue-600 underline hover:text-blue-700"
            >
              Ask.
            </button>
          </p>
        </div>

        {/* Bottom CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-6">
            Need a custom solution or want to see these revolutionary features in action?
          </p>
          <Button variant="outline" size="lg" onClick={onScheduleDemo} className="font-semibold">
            Schedule Live Demo
          </Button>
        </div>

        {/* Value Props */}
        <div className="grid md:grid-cols-3 gap-6 mt-16 max-w-5xl mx-auto">
          <div className="text-center">
            <div className="bg-linear-to-br from-blue-100 to-green-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <DollarSign className="w-8 h-8 text-green-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Built for Governance</h3>
            <p className="text-sm text-gray-600">
              Verifiable voting, financial transparency, and searchable meeting records built for
              community governance
            </p>
          </div>

          <div className="text-center">
            <div className="bg-linear-to-br from-purple-100 to-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Building2 className="w-8 h-8 text-purple-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Complete Transformation</h3>
            <p className="text-sm text-gray-600">
              Full community digital transformation with comprehensive training and support
            </p>
          </div>

          <div className="text-center">
            <div className="bg-linear-to-br from-indigo-100 to-purple-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
              <Users className="w-8 h-8 text-indigo-600" />
            </div>
            <h3 className="font-semibold text-gray-900 mb-2">Technical Support Team</h3>
            <p className="text-sm text-gray-600">
              Expert technical support. Response within 24 business hours on both tiers, with Gold
              in a priority queue.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
